import {
  Chip,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'

import GlyphIcon from 'components/common/GlyphIcon'
import Icon from 'components/Icon'
import { HaltButton } from 'components/navigation/Notifications/NotificationsList/HaltButton'
import {
  ICON_BACKGROUND_COLOR_RUNNING_TEST,
  NOTIFICATION_LEFT_PADDING
} from 'components/navigation/Notifications/utils'
import { isRunHalting } from 'components/scenarios/utils'
import { getStatusRunUrl } from 'components/services/dependency-utils'
import useAuth from 'hooks/use-auth'
import { useGetService } from 'hooks/use-services'
import theme from 'theme'
import { ReliabilityTestRun } from 'types/api/reliability-tests/runs'

export const ActiveReliabilityTestListItem: React.FC<{
  doFetch(): void
  onConfirmHalt(options: Record<string, any>): void
  reliabilityTestRun: ReliabilityTestRun
}> = ({ doFetch, onConfirmHalt, reliabilityTestRun, ...rest }) => {
  const { currentTeamId } = useAuth()
  const {
    dependencyId,
    guid: reliabilityTestId,
    run: latestRun,
    runNumber,
    serviceId
  } = reliabilityTestRun
  const scenarioId = latestRun.scenario_id
  const isHalting = isRunHalting(latestRun)
  const { data: service } = useGetService(serviceId)

  const handleConfirmRequest = ({ reason } = { reason: '' }) =>
    onConfirmHalt({
      attack: { guid: scenarioId },
      type: 'scenario',
      currentTeamId,
      reason,
      runNumber
    })

  const isDependencyTest = dependencyId && dependencyId.length > 0

  const primaryText = service?.name

  // if we want the name of the dependency in the future, we'll have to make a separate API call
  const secondaryText = `"${latestRun?.name}" is running...`

  return (
    <ListItem
      data-test-notification-item
      disablePadding
      component="div"
      secondaryAction={
        <HaltButton
          fetchAttack={doFetch}
          isHalting={isHalting}
          runInfo={latestRun}
          title="Halt RM Test"
          trackedEvent={
            isDependencyTest
              ? 'ServiceDependencyRMTestHalted'
              : 'ServiceRMTestHalted'
          }
          trackingOptions={{ scenarioId, runNumber }}
          type="scenario"
          onConfirmRequest={handleConfirmRequest}
        />
      }
      {...rest}
    >
      <ListItemButton
        component={Link}
        to={getStatusRunUrl({
          dependencyId,
          testId: reliabilityTestId,
          serviceId,
          runNumber
        })}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ pl: NOTIFICATION_LEFT_PADDING, pr: 4, width: '100%' }}
        >
          <GlyphIcon
            css={{
              marginRight: theme.space[3]
            }}
          >
            <Icon
              css={{
                fontSize: theme.fontSizes[3],
                color: ICON_BACKGROUND_COLOR_RUNNING_TEST
              }}
              icon="chartNetwork"
            />
          </GlyphIcon>

          <ListItemText
            data-test-notification-text
            primary={
              <>
                {primaryText}
                <Chip
                  label={isDependencyTest ? 'dependency' : 'service'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </>
            }
            secondary={
              <Typography variant="caption">{secondaryText}</Typography>
            }
          />
        </Stack>
      </ListItemButton>
    </ListItem>
  )
}
